/**
 * Подстраницы «готовых инструментов» Bafsy (/:lang/solutions/:slug).
 *
 * Hand-curated мирор `content/10-subproducts/*.md` — ровно как `READY_SOLUTIONS`
 * и `ENGINE_MODULES` в `home-data.ts` мирорят свои markdown-исходники.
 *
 * RU и EN копирайт хранятся бок о бок: RU на базовых полях (`heading`,
 * `lead`, …), EN — на параллельных `*En`-полях. Тон EN — confident,
 * active voice, B2B для FMCG / retail marketing-директоров (см. также
 * `[site/src/i18n/locales/en.ts](src/i18n/locales/en.ts)`).
 *
 * Источник копирайта (RU): `assets/text.txt` (см. также
 * `content/10-subproducts/<slug>.md` — текстовое зеркало).
 */

import type { SubproductItem } from '@/types/component-types';

export const SUBPRODUCTS: readonly SubproductItem[] = [
  /* ------------------------------------------------------------------------ */
  /* 1. Лендинг                                                               */
  /* ------------------------------------------------------------------------ */
  {
    id: 'landing',
    heading: 'Разработка промо-лендингов под ключ — быстро и с высоким конверсионным потенциалом',
    headingEn: 'Turnkey promo landing pages — launched fast, built for conversion',
    lead: 'Создаем эффективные одностраничные сайты для промоакций, которые запускаются в короткие сроки и дают максимум заявок и участия.',
    leadEn:
      'High-converting single-page promo sites that go live in days and deliver the maximum number of registrations and entries.',
    primaryCta: {
      label: 'Запустить лендинг от 2х дней',
      labelEn: 'Launch in 2 days',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'why',
        title: 'Почему Bafsy',
        titleEn: 'Why Bafsy',
        kind: 'bullets',
        items: [
          'Собственный конструктор лендингов позволяет существенно экономить время и бюджет клиента.',
          'Экспресс-режим: полноценный промо-лендинг может быть запущен за несколько часов.',
          'Глубокая интеграция с собственной платформой Promokit — все механики промоакций работают «из коробки».',
          '900+ успешно запущенных промо-лендингов за 8 лет.',
        ],
        itemsEn: [
          'Our in-house landing builder cuts both time and budget for the client.',
          'Express mode: a full-featured promo landing can go live within hours.',
          'Deep integration with our Promokit platform — every promo mechanic works out of the box.',
          '900+ promo landings successfully launched over 8 years.',
        ],
      },
      {
        id: 'workflow',
        title: 'Как мы работаем',
        titleEn: 'How we work',
        kind: 'numbered',
        items: [
          'Бриф и постановка целей',
          'Разработка структуры и дизайна',
          'Интеграция механики (коды, чеки, конкурсы)',
          'Тестирование и запуск',
          'Постпроектная поддержка и доработки',
        ],
        itemsEn: [
          'Brief intake and goal setting',
          'Structure and visual design',
          'Mechanic integration (codes, receipts, contests)',
          'QA and go-live',
          'Post-launch support and iterations',
        ],
      },
      {
        id: 'capabilities',
        title: 'Возможности лендингов Bafsy',
        titleEn: 'What Bafsy landings can do',
        kind: 'bullets',
        items: [
          'Адаптивный современный дизайн',
          'Интеграция с Яндекс.Метрикой и настройка целей',
          'Формы регистрации и авторизации, включая вход через социальные сети',
          'Мобильная версия с отличным UX',
          'Подключение любых промо-механик Promokit',
          'SEO-оптимизация даже для краткосрочных акций',
        ],
        itemsEn: [
          'Modern responsive design',
          'Yandex.Metrica integration with goal tracking',
          'Sign-up and login forms, including social login',
          'Mobile experience built for great UX',
          'Plug in any Promokit promo mechanic',
          'SEO-ready even for short-lived campaigns',
        ],
      },
      {
        id: 'audience',
        title: 'Для кого подойдёт',
        titleEn: 'Who it fits',
        kind: 'prose',
        paragraph:
          'FMCG-бренды, производители товаров повседневного спроса, торговые сети и компании, регулярно запускающие масштабные потребительские промоакции.',
        paragraphEn:
          'FMCG brands, manufacturers of everyday consumer goods, retail chains and any company that runs large-scale consumer promotions on a regular basis.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Сколько времени занимает разработка промо-лендинга?',
            answer:
              'В разных тарифах взаимодействия сроки могут отличаться. Экспресс-лендинг можно запустить за несколько часов, лендинг под ключ в среднем за 3 недели. Сроки всегда индивидуальны и зависят от ТЗ',
          },
          {
            title: 'Можно ли интегрировать лендинг с существующим сайтом бренда?',
            answer: 'Да, мы можем интегрировать лендинг с существующим сайтом бренда',
          },
          {
            title: 'Предоставляете ли поддержку после запуска?',
            answer:
              'Наша команда осуществляет круглосуточную техническую поддержку вашего лендинга, а также к каждому проекту подключен персональный менеджер, который поможет решить любой вопрос',
          },
        ],
        itemsEn: [
          {
            title: 'How long does a promo landing take to build?',
            answer:
              'In different interaction tiers, timelines can vary. An express landing can be launched in a few hours, a full-featured landing in about 3 weeks on average. Timelines are always individual and depend on the TZ',
          },
          {
            title: 'Can the landing be integrated with the existing brand site?',
            answer: 'Yes, we can integrate the landing with the existing brand site',
          },
          {
            title: 'Do you offer post-launch support?',
            answer:
              'Our team provides 24/7 technical support for your landing, and each project is assigned a dedicated manager who will help with any questions',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы запустить эффективный промо-лендинг в сжатые сроки?',
      headingEn: 'Ready to launch a high-impact promo landing on a tight timeline?',
      lead: 'Напишите нам — ответим в течение 1 часа.',
      leadEn: 'Drop us a line — we reply within an hour.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 2. Промо-акция с кодовой механикой                                       */
  /* ------------------------------------------------------------------------ */
  {
    id: 'promo-code',
    heading: 'Промоакции с кодовой механикой на собственной платформе Promokit',
    headingEn: 'Code-based promotions on the Promokit platform',
    lead: 'Запускайте масштабные акции «Код под крышкой / в упаковке» с удобной загрузкой кодов, автоматическим начислением баллов и розыгрышами.',
    leadEn:
      'Run large-scale "code under the cap / inside the pack" campaigns with smooth code submission, automatic point accrual and prize draws.',
    primaryCta: {
      label: 'Запустить кодовую акцию',
      labelEn: 'Launch a code-based promo',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'advantages',
        title: 'Преимущества',
        titleEn: 'Advantages',
        kind: 'bullets',
        items: [
          'Собственная платформа Promokit, проверенная 8 годами активной эксплуатации',
          'Полный цикл: от регистрации до публикации победителей',
          'Маркетинговые рассылки (email + SMS)',
          'Коробочные и кастомные игры',
          'Кешбэк и начисление баллов',
          'ИИ в обработке обращений',
          'Гибкая настройка вывода победителей',
          'Бюджетнее чем чековая механика',
        ],
        itemsEn: [
          'Our Promokit platform — battle-tested over 8 years in production',
          'End-to-end flow: from sign-up to publishing the winners',
          'Marketing outreach (email + SMS)',
          'Out-of-the-box and custom games',
          'Cashback and point accrual',
          'AI assisting with participant support',
          'Flexible winner-announcement setup',
          'More budget-friendly than receipt mechanics',
        ],
      },
      {
        id: 'how-it-works',
        title: 'Как это работает',
        titleEn: 'How it works',
        kind: 'numbered',
        items: [
          'Участник вводит код',
          'Система мгновенно проверяет уникальность',
          'Начисляются баллы, призы или шансы на участие в розыгрыше',
          'Участник участвует в розыгрыше или получает мгновенный приз',
        ],
        itemsEn: [
          'The participant submits a code',
          'The system instantly checks uniqueness',
          'Points, prizes or prize-draw entries are awarded',
          'The participant joins the draw or receives an instant prize',
        ],
      },
      {
        id: 'capabilities',
        title: 'Возможности Promokit для кодовых акций',
        titleEn: 'Promokit capabilities for code-based promos',
        kind: 'bullets',
        items: [
          'Авторизация через соцсети, телефон или email',
          'Гибкие условия акции',
          'Аналитика в реальном времени',
          'Защита от фрода',
          'Интеграция с CRM бренда',
        ],
        itemsEn: [
          'Sign-in via social, phone or email',
          'Flexible campaign rules',
          'Real-time analytics',
          'Fraud protection',
          'Integration with the brand CRM',
        ],
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Для кого подходит кодовая механика?',
            answer:
              'Кодовая механика идеальное решение для брендов с возможностью нанесения уникального кода под упаковку',
          },
          {
            title: 'Как долго проверяются коды?',
            answer:
              'Коды валидируются практически мгновенно, что позволяет проводить розыгрыши в тот же день',
          },
          {
            title: 'Кто создает базу кодов?',
            answer:
              'Базу уникальных кодов можем создать мы, но если у вас есть возможности создать базу самостоятельно, то мы готовы передать это на вашу сторону и поделиться рекомендациями',
          },
        ],
        itemsEn: [
          {
            title: 'What is the ideal audience for code-based promotions?',
            answer:
              'Code-based mechanics are perfect for brands with the ability to apply unique codes under the packaging',
          },
          {
            title: 'How long does it take to validate codes?',
            answer: 'Codes are validated almost instantly, allowing for same-day prize draws',
          },
          {
            title: 'Who creates the code base?',
            answer:
              'We can create the unique code base, but if you have the ability to create it yourself, we are happy to transfer it to your side and share recommendations',
          },
        ],
      },
    ],
    closing: {
      heading: 'Результаты с Bafsy',
      headingEn: 'Results with Bafsy',
      lead: 'Более 900 реализованных промо-сайтов, 249+ постоянных клиентов, успешный опыт проведения акций по всей России и за рубежом.',
      leadEn:
        'Over 900 promo sites delivered, 249+ recurring clients, and a track record of campaigns across Russia and beyond.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 3. Промо-акция с чековой механикой                                       */
  /* ------------------------------------------------------------------------ */
  {
    id: 'promo-receipt',
    heading: 'Промоакции с загрузкой чеков — с интеграцией ФНС и проверкой подлинности',
    headingEn:
      'Receipt-based promotions with Federal Tax Service integration and authenticity checks',
    lead: 'Максимально прозрачные и удобные акции «Купи и загрузи чек» с автоматической валидацией через ФНС.',
    leadEn:
      'Fully transparent, frictionless "buy and upload your receipt" campaigns with automatic Federal Tax Service validation.',
    primaryCta: {
      label: 'Запустить чековую акцию',
      labelEn: 'Launch a receipt-based promo',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'key-advantages',
        title: 'Ключевые преимущества',
        titleEn: 'Key advantages',
        kind: 'bullets',
        items: [
          'Готовая интеграция с ФНС для проверки существования и валидности чека',
          'Полноценная платформа Promokit',
          'Мотивационные механики для сотрудников торговых точек',
          'Загрузка фото/QR чека',
          'ИИ в обработке обращений',
          'Автоматический запрос призов',
          'Коробочные и кастомные игры + кешбэк',
        ],
        itemsEn: [
          'Ready-made Federal Tax Service integration to check that a receipt exists and is valid',
          'Full-featured Promokit platform',
          'Incentive mechanics for retail-point staff',
          'Photo or QR receipt upload',
          'AI assisting with participant support',
          'Automatic prize claims',
          'Out-of-the-box and custom games plus cashback',
        ],
      },
      {
        id: 'features',
        title: 'Особенности чековых акций Bafsy',
        titleEn: 'What sets Bafsy receipt promos apart',
        kind: 'bullets',
        items: [
          'Высокий уровень доверия участников',
          'Удобный личный кабинет',
          'Защита от мошенничества',
          'Гибкие условия (минимальная сумма чека, категории товаров и т.д.)',
        ],
        itemsEn: [
          'High level of participant trust',
          'A convenient participant dashboard',
          'Fraud protection',
          'Flexible rules (minimum receipt amount, product categories and more)',
        ],
      },
      {
        id: 'audience',
        title: 'Для каких брендов идеально',
        titleEn: 'A perfect fit for',
        kind: 'prose',
        paragraph: 'FMCG-бренды и любые категории товаров, реализуемые через розничные сети.',
        paragraphEn: 'FMCG brands and any product category sold through retail chains.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Сколько проверяются чеки? ',
            answer: 'Чеки валидируются ФНС в течение 72 часов',
          },
          {
            title: 'Принимаются ли чеки с маркетплейсов?',
            answer:
              'Да, наша платформа может распознавать чеки с маркетплейсов и фильтровать их в случае загрузки авансовых чеков',
          },
          {
            title: 'Что происходит с отклоненными в ФНС чеками?',
            answer:
              'Все чеки со статусом “отклонен” мы проверяем вручную на случай низкого качества изображения. Если оно не распозналось в ФНС, то мы проверим вручную соответствие Правилам Акции',
          },
        ],
        itemsEn: [
          {
            title: 'How long does it take to validate receipts?',
            answer: 'Receipts are validated by the Federal Tax Service in 72 hours',
          },
          {
            title: 'Are marketplaces accepted?',
            answer:
              'Yes, our platform can recognize receipts from marketplaces and filter them in case of uploading advance receipts',
          },
          {
            title: 'What happens to rejected receipts in the Federal Tax Service?',
            answer:
              'All receipts with the status “rejected” are manually checked by us on the case of low image quality. If it is not recognized by the Federal Tax Service, we will manually check the compliance with the rules of the promotion',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы запустить чековую акцию?',
      headingEn: 'Ready to launch a receipt-based promo?',
      lead: 'Расскажем о готовых шаблонах и подберём механику под ваши категории товаров.',
      leadEn:
        'We will walk you through ready-made templates and pick the mechanic that fits your product categories.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 4. Промо-акция с конкурсной механикой                                    */
  /* ------------------------------------------------------------------------ */
  {
    id: 'promo-contest',
    heading: 'Фото- и видео-конкурсы с голосованием и модерацией',
    headingEn: 'Photo and video contests with voting and moderation',
    lead: 'Организуем яркие пользовательские конкурсы, где участники присылают фото/видео, голосуют и выигрывают призы.',
    leadEn: 'Vivid user contests where participants submit photos or videos, vote and win prizes.',
    primaryCta: {
      label: 'Запустить конкурс',
      labelEn: 'Launch a contest',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'offering',
        title: 'Что мы предлагаем',
        titleEn: 'What we offer',
        kind: 'bullets',
        items: [
          'Конкурсы с открытым и закрытым голосованием',
          'Выделенная команда профессиональных модераторов контента',
          'Интеграция генеративного ИИ',
          'Удобная загрузка работ',
          'Лидерборды',
          'Автоматическое определение победителей',
        ],
        itemsEn: [
          'Contests with open or closed voting',
          'A dedicated team of professional content moderators',
          'Generative AI integration',
          'Smooth submission upload',
          'Leaderboards',
          'Automatic winner selection',
        ],
      },
      {
        id: 'advantages',
        title: 'Преимущества',
        titleEn: 'Advantages',
        kind: 'bullets',
        items: [
          'Высокая виральность и вовлеченность',
          'Генерация UGC-контента для бренда',
          'Защита от фрода',
          'Интеграция с социальными сетями',
        ],
        itemsEn: [
          'High virality and engagement',
          'A steady stream of UGC for the brand',
          'Fraud protection',
          'Social network integration',
        ],
      },
      {
        id: 'audience',
        title: 'Идеально для',
        titleEn: 'Ideal for',
        kind: 'prose',
        paragraph:
          'Брендов, которые хотят не просто раздать призы, а получить яркий пользовательский контент и эмоциональную связь с аудиторией.',
        paragraphEn:
          'Brands that aim not just to hand out prizes but to capture vivid user content and build an emotional bond with their audience.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Для каких проектов применимо?',
            answer:
              'Отлично работает на вовлечение аудитории без применения чековых и кодовых механик',
          },
          {
            title: 'На каких площадках лучше всего использовать?',
            answer:
              'Зависит непосредственно  от вашей ЦА можно выбрать социальные сети, отдельный лендинг или чат-бот',
          },
          {
            title: 'Сколько времени занимает реализация?',
            answer: 'Реализация занимает в среднем 3 недели в зависимости от сложности механики',
          },
        ],
        itemsEn: [
          {
            title: 'What is the ideal audience for photo and video contests?',
            answer:
              'Photo and video contests are perfect for brands that want to engage their audience without using receipt or code-based mechanics',
          },
          {
            title: 'What platforms are best suited for photo and video contests?',
            answer:
              'It depends directly on your target audience. You can choose social networks, a separate landing or a chatbot',
          },
          {
            title: 'How long does it take to implement?',
            answer:
              'Implementation takes an average of 3 weeks, depending on the complexity of the mechanic',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы собрать UGC-волну вокруг бренда?',
      headingEn: 'Ready to spark a UGC wave around your brand?',
      lead: 'Покажем кейсы и предложим механику под ваши цели и бюджет.',
      leadEn: 'We will share cases and propose a mechanic that fits your goals and budget.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 5. Чат-бот                                                               */
  /* ------------------------------------------------------------------------ */
  {
    id: 'chatbot',
    heading: 'Чат-боты для промоакций в мессенджерах',
    headingEn: 'Chatbots for promotions inside messengers',
    lead: 'Запускайте промоакции полностью или частично внутри Telegram, VK и других мессенджеров.',
    leadEn: 'Run promotions partly or fully inside Telegram, VK and other messengers.',
    primaryCta: {
      label: 'Запустить чат-бота',
      labelEn: 'Launch a chatbot',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'capabilities',
        title: 'Возможности',
        titleEn: 'Capabilities',
        kind: 'bullets',
        items: [
          'Чат как основной канал взаимодействия',
          'Гибридное решение «Бот + сайт» — участник сам выбирает удобный формат',
          'Полная механика промо внутри бота (загрузка кодов/чеков, игры, розыгрыши)',
          'Интеграция с Promokit',
          'Автоматические рассылки',
          'ИИ-ассистент для ответов на вопросы участников',
          'Персонализация общения',
        ],
        itemsEn: [
          'Chat as the primary channel of interaction',
          'Hybrid "bot + site" setup — the participant picks whichever they prefer',
          'The full promo mechanic inside the bot (code or receipt upload, games, prize draws)',
          'Promokit integration',
          'Automated broadcasts',
          'AI assistant that answers participant questions',
          'Personalised conversations',
        ],
      },
      {
        id: 'advantages',
        title: 'Преимущества чат-ботов Bafsy',
        titleEn: 'What sets Bafsy chatbots apart',
        kind: 'bullets',
        items: [
          'Бюджетнее чем лендинг',
          'Снижение нагрузки на поддержку',
          'Упрощенный порог входа в промо',
          'Активная база контактов для дальнейшей коммуникации',
          'Кастомизированные сценарии с высокой конверсией',
        ],
        itemsEn: [
          'More budget-friendly than a landing page',
          'Lower load on the support team',
          'A lower barrier to entry for participants',
          'An active contact base for follow-up communication',
          'Custom scenarios with high conversion',
        ],
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'В чем удобство чат-бота для пользователя?',
            answer:
              'Чат-бот в социальных сетях позволяет упростить точку входа для пользователей в комфортной для них социальной сети',
          },
          {
            title: 'Удобно ли пользователю участвовать в Акции в чат-боте?',
            answer:
              'Мы создали комфортный и удобный путь пользователя, который можно адаптировать в зависимости от ваших пожеланий',
          },
          {
            title: 'Отличаются ли сроки реализации чат-бота от лендинга?',
            answer: 'Чат-бот реализуется в среднем на 30% быстрее чем лендинг',
          },
        ],
        itemsEn: [
          {
            title: 'What is the advantage of a chatbot for the user?',
            answer:
              'A chatbot in social networks allows you to simplify the entry point for users in a comfortable social network',
          },
          {
            title: 'Is it convenient for the user to participate in the promotion in the chatbot?',
            answer:
              'We have created a comfortable and easy path for the user, which can be adapted depending on your wishes',
          },
          {
            title: 'Are the implementation times of a chatbot different from a landing?',
            answer: 'Chatbot implementation takes on average 30% faster than a landing',
          },
        ],
      }
    ],
    closing: {
      heading: 'Готовы запустить промо в мессенджерах?',
      headingEn: 'Ready to launch a promo inside messengers?',
      lead: 'Спроектируем сценарий бота и подключим его к движку Promokit.',
      leadEn: 'We will design the bot scenario and wire it up to the Promokit engine.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 6. Сайт                                                                  */
  /* ------------------------------------------------------------------------ */
  {
    id: 'site',
    heading: 'Разработка полноценных многостраничных сайтов для брендов и продуктовых линеек',
    headingEn: 'Full-scale multi-page sites for brands and product lines',
    lead: 'Создаем современные корпоративные сайты, сайты брендов и продуктов с помощью готовых шаблонов и блоков или полностью кастомно.',
    leadEn:
      'We build modern corporate, brand and product sites — from ready-made templates and blocks to fully custom designs.',
    primaryCta: {
      label: 'Обсудить сайт',
      labelEn: 'Discuss your site',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Связаться с командой',
      labelEn: 'Talk to the team',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'cooperation',
        title: 'Варианты сотрудничества',
        titleEn: 'Ways to work with us',
        kind: 'bullets',
        items: [
          'Получить доступ к конструктору и собрать сайт самостоятельно',
          'Заказать полный цикл разработки под ключ силами нашей команды',
          'Редизайн и развитие существующего сайта',
        ],
        itemsEn: [
          'Get access to the builder and assemble the site yourself',
          'Order full turnkey development by our team',
          'Redesign and grow an existing site',
        ],
      },
      {
        id: 'included',
        title: 'Что входит',
        titleEn: 'What is included',
        kind: 'bullets',
        items: [
          'Современный адаптивный дизайн',
          'Быстрая и безопасная CMS',
          'Интеграция с CRM, аналитикой, платежами',
          'SEO-оптимизация',
          'Обновленная удобная административная панель',
        ],
        itemsEn: [
          'Modern responsive design',
          'A fast, secure CMS',
          'Integration with CRM, analytics and payments',
          'SEO optimisation',
          'A modernised, easy-to-use admin panel',
        ],
      },
      {
        id: 'audience',
        title: 'Для кого',
        titleEn: 'Who it is for',
        kind: 'prose',
        paragraph:
          'Современные бренды, ориентированные на сильное цифровое представительство, запуск новых продуктовых линеек и долгосрочную коммуникацию с потребителями.',
        paragraphEn:
          'Modern brands focused on a strong digital presence, launching new product lines and building long-term communication with their customers.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Сколько занимает реализация? ',
            answer:
              'Реализация многостраничного сайта занимает в среднем 4 недели в зависимости от ТЗ',
          },
          {
            title: 'Можно ли использовать полностью свой дизайн?',
            answer: 'Конечно, мы можем собрать сайт по вашему дизайну',
          },
          {
            title: 'Предоставляется ли техподдержка сайта?',
            answer:
              'Техническая поддержка работоспособности сайта предоставляется на весь период его действия',
          },
        ],
        itemsEn: [
          {
            title: 'How long does it take to implement a multi-page site?',
            answer:
              'Implementation of a multi-page site takes an average of 4 weeks, depending on the TZ',
          },
          {
            title: 'Can you use your own design completely?',
            answer: 'Of course, we can build a site according to your design',
          },
          {
            title: 'Is technical support provided for the site?',
            answer:
              'Technical support for the site’s functionality is provided throughout its operation',
          },
        ],
      }
    ],
    closing: {
      heading: 'Готовы обсудить ваш сайт?',
      headingEn: 'Ready to talk about your site?',
      lead: 'Подберём формат — конструктор, кастом или редизайн — и составим план работ.',
      leadEn:
        'We will pick the format — builder, custom or redesign — and put together a work plan.',
    },
  },
];

/** Карта slug → SubproductItem (для O(1)-доступа из роутера и страницы). */
export const SUBPRODUCT_BY_ID: Readonly<Record<string, SubproductItem>> = Object.freeze(
  Object.fromEntries(SUBPRODUCTS.map((s) => [s.id, s])),
);

export const SUBPRODUCT_IDS: readonly string[] = SUBPRODUCTS.map((s) => s.id);

/** Type guard — пригодится в роутер-гарде и страничном lookup. */
export function isSubproductId(value: string): boolean {
  return Object.prototype.hasOwnProperty.call(SUBPRODUCT_BY_ID, value);
}
